// OWNED BY the meetings tab agent (created under detail/tabs/meetings/).
//
// The listing chips on a meeting row. A meeting can be linked to several
// properties (a viewing tour); the legacy calendar row showed listingsData[0]
// and a bare "+N", so the realtor could see that more existed but never what
// they were. Here:
//
//   - as many address chips as the row width allows are rendered (count is
//     breakpoint-driven — see VISIBLE_CHIPS — NOT pixel-measured, so it can't
//     drift with fonts, zoom or RTL metrics);
//   - the remainder collapses into ONE explicit "+N" chip — nothing is ever
//     dropped silently, including listings we can't label (see
//     MeetingListings.unlabeledCount);
//   - "+N" reveals the rest two ways: hover/focus → tooltip naming every hidden
//     address, click → expands them in place under the visible ones.
//
// Why click-expands rather than click-opens-a-listing-dialog: the row itself is
// already a click target (it opens the meeting editor, which lists every linked
// listing). Giving "+N" a second navigation would compete with that; an in-place
// expand answers "which properties?" without leaving the list. The plain chips
// stay non-interactive on purpose so a click on one still bubbles to the row and
// opens the meeting editor, exactly as before. Only the "+N" toggle stops
// propagation.

import { useState } from "react";
import { Chip, Stack, Tooltip, Typography } from "@material-ui/core";
import { useContactSurfaceDown } from "../../contactSurface";
import type { MeetingListingChip, MeetingListings } from "./meetingsData";

// Chips visible before the "+N" kicks in. The tab card is capped at 880px, so
// only the mobile breakpoint really changes the space available — the lg step
// just buys the third chip once the viewport stops squeezing the card.
const VISIBLE_CHIPS = { narrow: 1, medium: 2, wide: 3 } as const;

// Per-chip cap: the more chips share the row, the tighter each one, so the title
// column keeps roughly half the row. Truncation is safe here — every chip has a
// tooltip carrying its full address + price.
const CHIP_MAX_WIDTH = { narrow: 120, medium: 150, wide: 132 } as const;

type MeetingListingChipsProps = {
  listings: MeetingListings;
};

export function MeetingListingChips({ listings }: MeetingListingChipsProps): JSX.Element | null {
  // Same mobile switch the page's TabCard uses, so the row and its container
  // change shape at the same width — and, like TabCard, measured against the SURFACE so
  // that holds inside the CRM panel too (`contactSurface.tsx`).
  const isNarrow = useContactSurfaceDown("sm");
  const isMedium = useContactSurfaceDown("lg");

  const [expanded, setExpanded] = useState(false);

  const { chips, unlabeledCount } = listings;

  if (!chips.length && !unlabeledCount) return null;

  const width = isNarrow ? "narrow" : isMedium ? "medium" : "wide";
  const chipMaxWidth = CHIP_MAX_WIDTH[width];

  const visible = chips.slice(0, VISIBLE_CHIPS[width]);
  const hidden = chips.slice(VISIBLE_CHIPS[width]);
  const overflowCount = hidden.length + unlabeledCount;

  // One listing keeps the legacy "address (price)" label. With several, the
  // price is dropped so more addresses stay legible — the tooltip still has it.
  const showPrice = chips.length === 1;

  const renderChip = (chip: MeetingListingChip): JSX.Element => (
    <Tooltip key={chip.key} title={chip.full} enterDelay={400}>
      <Chip
        size="small"
        variant="outlined"
        dir="auto"
        label={showPrice ? `${chip.address}${chip.priceSuffix}` : chip.address}
        sx={{ maxWidth: chipMaxWidth, flexShrink: 0 }}
      />
    </Tooltip>
  );

  const unlabeledLine =
    unlabeledCount === 1 ? "ועוד נכס אחד ללא כתובת" : `ועוד ${unlabeledCount} נכסים ללא כתובת`;

  const overflowTooltip = expanded ? (
    "הסתרת הנכסים הנוספים"
  ) : (
    <Stack gap={0.25}>
      {hidden.map((chip) => (
        <Typography key={chip.key} variant="caption" dir="auto">
          {chip.full}
        </Typography>
      ))}
      {unlabeledCount > 0 && <Typography variant="caption">{unlabeledLine}</Typography>}
    </Stack>
  );

  return (
    <Stack alignItems="flex-end" gap={0.5} sx={{ flexShrink: 0 }}>
      <Stack direction="row" alignItems="center" gap={0.5}>
        {visible.map(renderChip)}

        {overflowCount > 0 && (
          <Tooltip title={overflowTooltip} enterDelay={200}>
            <Chip
              size="small"
              variant={expanded ? "filled" : "outlined"}
              color="primary"
              label={expanded ? "הסתרה" : `+${overflowCount}`}
              aria-expanded={expanded}
              aria-label={
                expanded ? "הסתרת הנכסים הנוספים" : `הצגת ${overflowCount} נכסים נוספים`
              }
              onClick={(event) => {
                // The row opens the meeting editor — this toggle must not.
                event.stopPropagation();
                setExpanded((previous) => !previous);
              }}
              sx={{ flexShrink: 0 }}
            />
          </Tooltip>
        )}
      </Stack>

      {expanded && hidden.length > 0 && (
        <Stack direction="row" flexWrap="wrap" justifyContent="flex-end" gap={0.5}>
          {hidden.map(renderChip)}
        </Stack>
      )}
    </Stack>
  );
}
